import type { Task } from "@app/core";
import type { DayCol } from "./build-days.js";

export const SNAP_MIN = 15;
export const DAY_MIN = 24 * 60;
export const LONG_PRESS_MS = 350;
export const MOVE_SLOP_PX = 10;
export const HORIZONTAL_PAN_SLOP_PX = 8;
/** One day-count step per this much pinch-span change (horizontal). */
export const DAYS_STEP_PX = 70;
/** A pan may only START within this centered fraction of the window width — the
 *  outer band is reserved for the sidebar (left) / inbox (right) edge-swipes. */
export const PAN_CENTER_FRAC = 0.85;
// NOTE: the horizontal-pan feel knobs (amplification, fling projection, page-flip
// threshold, max step) are now DEBUG-TUNABLE settings — see settings.store.ts
// (`touchPan*` / TOUCH_* constants) and useTouchCalendarGestures.
export const AUTOSCROLL_ZONE_PX = 48;
export const AUTOSCROLL_STEP_PX = 9;
/** Edge day-paging while DRAGGING a block (long-press move on touch, native
 *  HTML5 drag on desktop): hold the block in a strip at the grid's left/right
 *  edge to advance the day window. First step lands after EDGE_PAGE_FIRST_MS (a
 *  deliberate dwell — a quick drop on the edge day still works by releasing
 *  first), then repeats every EDGE_PAGE_MS. */
export const EDGE_PAGE_FIRST_MS = 500;
export const EDGE_PAGE_MS = 450;
/** Never let the edge strip exceed this fraction of the viewport on either side
 *  (keeps the middle droppable on 1–2 day views). */
export const EDGE_PAGE_MAX_FRAC = 0.25;
export const EDGE_PAGE_MIN_PX = 36;
/** Desktop uses a THIN fixed strip at the very grid edge instead of a column-
 *  wide zone: a mouse is precise (no fat-finger / grab-offset slack needed), so
 *  paging only fires when the cursor is jammed against the edge — most of the
 *  edge day stays comfortably droppable. */
export const EDGE_PAGE_DESKTOP_PX = 40;

/**
 * Width (px) of the auto-paging edge strip. Defaults to ONE full visible column:
 * a block dragged to the edge is a whole column wide, so wherever it was grabbed
 * the finger/cursor still falls inside a column-wide strip — fixed-px strips
 * missed when the block was grabbed off-center. Capped at a quarter of the
 * viewport so narrow (1–2 day) views keep a droppable middle.
 */
export function edgePageZonePx(viewportWidth: number, visibleCount: number): number {
  const colW = viewportWidth / Math.max(1, visibleCount);
  return Math.max(Math.min(colW, viewportWidth * EDGE_PAGE_MAX_FRAC), EDGE_PAGE_MIN_PX);
}

/** Live ghost of the block being long-press-moved on a touch device. */
export type TouchGhost =
  | { kind: "timed"; col: number; topMin: number; durationMin: number; title: string }
  | { kind: "all-day"; col: number; leftPx: number; topPx: number; widthPx: number; title: string };

/** Swallow the click the browser synthesizes after a consumed touch gesture. */
export function suppressNextClick(): void {
  const swallow = (e: Event) => { e.preventDefault(); e.stopPropagation(); };
  window.addEventListener("click", swallow, { capture: true, once: true });
  setTimeout(() => window.removeEventListener("click", swallow, { capture: true }), 500);
}

export function findTimedTask(days: DayCol[], id: string): Task | null {
  for (const d of days) for (const t of d.timed) if (t.id === id) return t;
  return null;
}

export function findCalendarTask(days: DayCol[], id: string): Task | null {
  for (const d of days) {
    for (const t of d.timed) if (t.id === id) return t;
    for (const t of d.tasks) if (t.id === id) return t;
  }
  return null;
}
